import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Crown, Lock, Check, Lightning, Robot, ChartLine, Handshake, Microphone, Palette, Storefront, ShieldCheck, X } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FEATURE_ICONS = {
  basic_matchmaking: Handshake, game_sessions: Lightning, basic_stats: ChartLine,
  rules_tutorials: Robot, ai_limited: Robot,
  catan_advanced_stats: ChartLine, ai_unlimited: Robot, advanced_matchmaking: Handshake,
  tournament_priority: Crown, profile_customization: Palette, voice_assistant: Microphone,
};

export default function PremiumPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [features, setFeatures] = useState({ free: [], premium: [] });
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubs] = useState([]);
  const [activeTab, setActiveTab] = useState('plans');
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [statusRes, featRes, plansRes, subsRes] = await Promise.all([
        axios.get(`${API_URL}/api/premium/status`, { withCredentials: true }),
        axios.get(`${API_URL}/api/premium/features`, { withCredentials: true }),
        axios.get(`${API_URL}/api/plans`, { withCredentials: true }),
        axios.get(`${API_URL}/api/subscriptions/my`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setStatus(statusRes.data);
      setFeatures(featRes.data);
      setPlans(plansRes.data.plans || []);
      setSubs(subsRes.data);
    } catch (err) { /* silent */ }
    finally { setLoading(false); }
  };

  const handleUpgrade = async (plan) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/payments/checkout`, {
        package_id: plan,
        origin_url: window.location.origin
      }, { withCredentials: true });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed');
    }
  };

  const handleCancel = async (planType) => {
    try {
      await axios.post(`${API_URL}/api/subscriptions/cancel`, { plan_type: planType }, { withCredentials: true });
      toast.success('Subscription cancelled');
      fetchData();
    } catch (err) { toast.error('Failed to cancel'); }
  };

  if (loading) return <div className="app-container flex items-center justify-center min-h-screen"><div className="w-10 h-10 border-4 border-[#9D4CDD] border-t-transparent rounded-full animate-spin" /></div>;

  const playerPlans = plans.filter(p => p.type === 'player');
  const shopPlans = plans.filter(p => p.type === 'shop');

  return (
    <div className="app-container" data-testid="premium-page">
      <header className="p-6 pb-3">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center" data-testid="back-button">
            <ArrowLeft size={20} weight="bold" className="text-white" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              <Crown size={24} weight="duotone" className="text-[#FACC15]" />
              CyBoard Premium
            </h1>
            <p className="text-[#71717A] text-xs">Unlock the full experience</p>
          </div>
        </div>

        <div className="flex gap-2">
          {['plans', 'features', 'manage'].map(t => (
            <button key={t} onClick={() => setActiveTab(t)}
              className={`flex-1 py-2 rounded-xl text-xs font-medium transition-all ${activeTab === t ? 'bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] text-white' : 'bg-[#181824] text-[#A1A1AA]'}`}
              data-testid={`tab-${t}`}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
      </header>

      <div className="px-6 pb-32 space-y-4">
        {/* Current Status Banner */}
        {status?.is_premium ? (
          <div className="p-4 rounded-xl bg-gradient-to-br from-[#9D4CDD]/20 to-[#FACC15]/10 border border-[#FACC15]/30" data-testid="premium-active-banner">
            <div className="flex items-center gap-2 mb-1">
              <Crown size={20} weight="fill" className="text-[#FACC15]" />
              <span className="text-white font-bold text-sm">Premium Active</span>
            </div>
            <p className="text-[#A1A1AA] text-xs">Expires: {status.premium_until ? new Date(status.premium_until).toLocaleDateString() : 'Never'}</p>
          </div>
        ) : (
          <div className="p-4 rounded-xl bg-[#0F0F16] border border-white/5" data-testid="ai-usage-banner">
            <p className="text-white text-sm font-medium mb-1">AI Usage Today</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-[#181824] rounded-full overflow-hidden">
                <div className="h-full bg-[#9D4CDD] rounded-full transition-all" style={{ width: `${((status?.ai_usage?.used || 0) / 10) * 100}%` }} />
              </div>
              <span className="text-[#A1A1AA] text-xs">{status?.ai_usage?.remaining ?? 0} left</span>
            </div>
          </div>
        )}

        {activeTab === 'plans' && (
          <>
            {/* Player Plans */}
            <div>
              <h3 className="text-white text-sm font-semibold mb-3 flex items-center gap-2">
                <Crown size={16} weight="duotone" className="text-[#FACC15]" />
                Player Plans
              </h3>
              {!status?.is_premium && (
                <div className="space-y-3">
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    className="p-4 rounded-xl bg-gradient-to-br from-[#9D4CDD]/10 to-[#7C3AED]/5 border border-[#9D4CDD]/30">
                    <h3 className="text-white font-bold text-sm mb-1">Premium Monthly</h3>
                    <p className="text-[#FACC15] text-2xl font-bold">$9.99<span className="text-[#71717A] text-xs font-normal">/month</span></p>
                    <ul className="mt-2 space-y-1">
                      <li className="text-[#A1A1AA] text-[10px] flex items-center gap-1"><Check size={10} className="text-[#39FF14]" /> Unlimited AI Assistant</li>
                      <li className="text-[#A1A1AA] text-[10px] flex items-center gap-1"><Check size={10} className="text-[#39FF14]" /> Advanced Catan Stats</li>
                      <li className="text-[#A1A1AA] text-[10px] flex items-center gap-1"><Check size={10} className="text-[#39FF14]" /> Tournament Priority</li>
                      <li className="text-[#A1A1AA] text-[10px] flex items-center gap-1"><Check size={10} className="text-[#39FF14]" /> Voice Assistant</li>
                    </ul>
                    <button onClick={() => handleUpgrade('premium_monthly')} className="btn-primary w-full mt-3 text-sm" data-testid="upgrade-monthly">Upgrade Now</button>
                  </motion.div>

                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                    className="p-4 rounded-xl bg-gradient-to-br from-[#FACC15]/10 to-[#F97316]/5 border border-[#FACC15]/30 relative overflow-hidden">
                    <span className="absolute top-2 right-2 text-[8px] px-2 py-0.5 bg-[#FACC15] text-[#0A0A12] rounded-full font-bold">SAVE 33%</span>
                    <h3 className="text-white font-bold text-sm mb-1">Premium Yearly</h3>
                    <p className="text-[#FACC15] text-2xl font-bold">$79.99<span className="text-[#71717A] text-xs font-normal">/year</span></p>
                    <button onClick={() => handleUpgrade('premium_yearly')} className="w-full mt-3 py-3 rounded-xl bg-[#FACC15] text-[#0A0A12] font-bold text-sm" data-testid="upgrade-yearly">Best Value</button>
                  </motion.div>
                </div>
              )}
            </div>

            {/* Shop Plans */}
            <div>
              <h3 className="text-white text-sm font-semibold mb-3 flex items-center gap-2">
                <Storefront size={16} weight="duotone" className="text-[#00F0FF]" />
                Shop Plans
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {shopPlans.map(p => (
                  <div key={p.id} className={`p-3 rounded-xl border ${p.id === 'paid_shop' ? 'bg-[#9D4CDD]/5 border-[#9D4CDD]/30' : 'bg-[#0F0F16] border-white/5'}`}>
                    <h4 className="text-white text-xs font-bold">{p.name}</h4>
                    <p className="text-[#FACC15] text-lg font-bold mt-1">${p.price}<span className="text-[#71717A] text-[9px] font-normal">/mo</span></p>
                    <ul className="mt-2 space-y-1">
                      {p.features.slice(0, 3).map(f => (
                        <li key={f} className="text-[#A1A1AA] text-[9px] flex items-center gap-1">
                          <Check size={8} className="text-[#39FF14]" /> {f.replace(/_/g, ' ')}
                        </li>
                      ))}
                      {p.features.length > 3 && <li className="text-[#71717A] text-[9px]">+{p.features.length - 3} more</li>}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {activeTab === 'features' && (
          <>
            <div>
              <h3 className="text-white text-sm font-semibold mb-3">Free Features</h3>
              {features.free.map(f => {
                const Icon = FEATURE_ICONS[f.id] || Lightning;
                return (
                  <div key={f.id} className="flex items-center gap-3 p-2 mb-1" data-testid={`feature-${f.id}`}>
                    <Check size={16} weight="bold" className="text-[#39FF14]" />
                    <Icon size={16} weight="duotone" className="text-[#A1A1AA]" />
                    <span className="text-[#A1A1AA] text-xs">{f.name}</span>
                  </div>
                );
              })}
            </div>

            <div>
              <h3 className="text-white text-sm font-semibold mb-3 flex items-center gap-2">
                <Crown size={16} weight="fill" className="text-[#FACC15]" />
                Premium Features
              </h3>
              {features.premium.map(f => {
                const Icon = FEATURE_ICONS[f.id] || Lightning;
                const unlocked = status?.is_premium;
                return (
                  <div key={f.id} className="flex items-center gap-3 p-2 mb-1" data-testid={`feature-${f.id}`}>
                    {unlocked ? <Check size={16} weight="bold" className="text-[#39FF14]" /> : <Lock size={16} weight="bold" className="text-[#71717A]" />}
                    <Icon size={16} weight="duotone" className={unlocked ? 'text-[#FACC15]' : 'text-[#71717A]'} />
                    <span className={`text-xs ${unlocked ? 'text-white' : 'text-[#71717A]'}`}>{f.name}</span>
                    {!unlocked && <span className="ml-auto text-[8px] px-1.5 py-0.5 bg-[#9D4CDD]/10 text-[#9D4CDD] rounded-full">PRO</span>}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {activeTab === 'manage' && (
          <div className="space-y-3">
            <h3 className="text-white text-sm font-semibold flex items-center gap-2">
              <ShieldCheck size={16} weight="duotone" className="text-[#39FF14]" />
              Active Subscriptions
            </h3>
            {subscriptions.length > 0 ? subscriptions.map((s, i) => (
              <div key={i} className="p-4 rounded-xl bg-[#0F0F16] border border-white/5" data-testid={`subscription-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-white text-sm font-medium">{s.plan_name}</h4>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${s.status === 'active' ? 'bg-[#39FF14]/10 text-[#39FF14]' : 'bg-[#EF4444]/10 text-[#EF4444]'}`}>
                    {s.status}
                  </span>
                </div>
                <p className="text-[#71717A] text-xs">Activated: {new Date(s.activated_at).toLocaleDateString()}</p>
                <p className="text-[#71717A] text-xs">Expires: {new Date(s.expires_at).toLocaleDateString()}</p>
                {s.status === 'active' && (
                  <button onClick={() => handleCancel(s.plan_type)} className="mt-2 text-[#EF4444] text-xs font-medium flex items-center gap-1" data-testid="cancel-sub-btn">
                    <X size={12} /> Cancel Subscription
                  </button>
                )}
              </div>
            )) : (
              <div className="text-center py-8">
                <ShieldCheck size={32} weight="duotone" className="text-[#71717A] mx-auto mb-2" />
                <p className="text-[#71717A] text-sm">No active subscriptions</p>
                <p className="text-[#A1A1AA] text-xs mt-1">Upgrade to Premium to unlock all features</p>
              </div>
            )}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
